import { cn } from "@/lib/utils"
import { Activity, BarChart3, Camera, Moon, Sun, Volume2, VolumeX, MoreHorizontal, LogOut, User, AlertTriangle, History } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useEffect, useState } from "react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useSound } from "@/hooks/use-sound"
import { triggerHapticFeedback } from "@/lib/haptics"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { useGymCheckins } from "@/hooks/useGymCheckins"
import { useAuth } from "@/context/AuthContext"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"

interface NavigationProps {
  activeTab: string
  onTabChange: (tab: string) => void
  soundEnabled: boolean
  setSoundEnabled: (enabled: boolean) => void
  onLogoClick?: () => void
}

export default function Navigation({ activeTab, onTabChange, soundEnabled, setSoundEnabled, onLogoClick }: NavigationProps) {
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [mounted, setMounted] = useState(false)
  const [showGuestExitDialog, setShowGuestExitDialog] = useState(false)
  const playNavSound = useSound("https://blob.v0.dev/sounds/swoosh.mp3", { soundEnabled, volume: 0.5 })
  const { isCheckedInToday, deleteTodaysCheckin } = useGymCheckins()
  const { isGuestMode, signOut } = useAuth()

  useEffect(() => {
    setMounted(true)
    // Simple theme detection without next-themes
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      setTheme('dark')
      document.documentElement.classList.add('dark')
    }
  }, [])

  const handleTabChange = (tab: string) => {
    onTabChange(tab)
    playNavSound()
    triggerHapticFeedback("light")
  }

  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark"
    setTheme(newTheme)
    localStorage.setItem('theme', newTheme)
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    triggerHapticFeedback("light")
  }

  const handleSoundToggle = () => {
    setSoundEnabled(!soundEnabled)
    triggerHapticFeedback("light")
  }

  const handleUncheck = async () => {
    if (confirm('Are you sure you want to uncheck from the gym? This is for testing purposes.')) {
      const success = await deleteTodaysCheckin()
      if (success) {
        triggerHapticFeedback("success")
      }
    }
  }

  const handleGuestExit = () => {
    if (isGuestMode) {
      setShowGuestExitDialog(true)
    } else {
      signOut()
    }
  }

  const confirmGuestExit = () => {
    signOut()
    setShowGuestExitDialog(false)
    triggerHapticFeedback("light")
  }

  const handleLogoClick = () => {
    onLogoClick?.()
    playNavSound()
    triggerHapticFeedback("light")
  }

  if (!mounted) {
    return <div className="h-16" /> // Placeholder to prevent layout shift
  }

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="flex items-center space-x-3 flex-shrink-0 hover:opacity-80 transition-opacity"
            >
              <div className="w-7 h-7 bg-foreground rounded-md flex items-center justify-center">
                <span className="text-background font-bold text-sm">A</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-lg font-semibold tracking-tight">Altitude</span>
                {isGuestMode && (
                  <Badge variant="secondary" className="text-xs font-medium">
                    Guest
                  </Badge>
                )}
              </div>
            </button>

            {/* Main Navigation Tabs */}
            <div className="flex items-center space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "flex items-center space-x-2 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150",
                    activeTab === tab.id
                      ? "text-foreground bg-accent"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="hidden sm:block">{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Controls - Desktop */}
            <div className="hidden sm:flex items-center space-x-1">
              <div className="w-px h-4 bg-border mx-2" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleSoundToggle} className="w-8 h-8 p-0">
                    {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle Sounds</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" onClick={handleThemeToggle} className="w-8 h-8 p-0">
                    <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle Theme</p>
                </TooltipContent>
              </Tooltip>

              {isGuestMode && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={handleGuestExit} className="w-8 h-8 p-0">
                      <User className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Exit Guest Mode</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Controls - Mobile (Dropdown) */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleSoundToggle} className="flex items-center gap-2">
                    {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                    {soundEnabled ? "Disable Sounds" : "Enable Sounds"}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleThemeToggle} className="flex items-center gap-2">
                    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </DropdownMenuItem>
                  {isCheckedInToday() && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleUncheck} className="flex items-center gap-2 text-red-600 dark:text-red-400">
                        <LogOut className="h-4 w-4" />
                        Uncheck from Gym
                      </DropdownMenuItem>
                    </>
                  )}
                  {isGuestMode && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleGuestExit} className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Exit Guest Mode
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Guest Exit Confirmation Dialog */}
      <Dialog open={showGuestExitDialog} onOpenChange={setShowGuestExitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
              </div>
              <DialogTitle className="text-xl font-semibold">Exit Guest Mode?</DialogTitle>
            </div>
            <DialogDescription className="text-left space-y-3">
              <p className="text-base">
                You're about to exit guest mode. This will permanently delete all your data including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                <li>Exercise logs and personal records</li>
                <li>Gym check-ins and streak progress</li>
                <li>Custom exercises you've created</li>
                <li>Progress photos and statistics</li>
              </ul>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                💡 <strong>Want to keep your data?</strong> Create an account instead to save your progress permanently.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowGuestExitDialog(false)}
              className="sm:mr-2"
            >
              Keep Using Guest Mode
            </Button>
            <Button
              variant="destructive"
              onClick={confirmGuestExit}
              className="bg-red-600 hover:bg-red-700"
            >
              Exit and Delete Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}

const tabs = [
  { id: "log", label: "Log", icon: Activity },
  { id: "progress", label: "Progress", icon: BarChart3 },
  { id: "history", label: "History", icon: History },
  { id: "photos", label: "Photos", icon: Camera },
]